import { useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAtomValue } from "jotai";
import { Button, ErrorState, Icon, Spinner, Title } from "@common/index";
import { Alert } from "@common/Alert";
import { ComponentColor, ComponentSize } from "@common/types";
import { useOrderApi } from "@/hooks/apiHooks/useOrderApi";
import { useMountRead } from "@/hooks/useMountRead";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import { useSessionSetAtom } from "@/hooks/useSessionSetAtom";
import {
    adminOrderDetailAtom,
    feeChargeBlocksAtom,
    isFetchingAdminOrderAtom,
    isProcessingOrderAtom,
} from "@/store/orderAtoms";
import {
    OrderStatusType,
    PaymentMethodType,
    PaymentStatusType,
} from "@models/order";
import type { TransitionOrderRequest } from "@models/order";
import {
    formatDateTime,
    currencyFormatter,
    formatPiecePricingFromCount,
} from "@utils/formatter";
import { failedChangeMessage } from "@utils/failedChangeMessage";
import { asApiFailure } from "@utils/apiFailure";
import { isSameGuid, normalizeGuid } from "@utils/sameGuid";
import { ROUTES } from "@routing/routes";
import { useNotification } from "@components/Notification/hooks/useNotification";
import { StatusBadge } from "./_components/StatusBadge";
import { DetailField } from "@admin/Clients/_components/DetailField";
import { DetailSection } from "@admin/Clients/_components/DetailSection";
import {
    ALLOWED_TRANSITIONS,
    type OrderTransitionAction,
} from "./_components/transitions";
import { paymentMethodLabel, paymentStatusLabel } from "./_components/status";
import { ShippedModal } from "./_components/ShippedModal";
import { ReturnedPaymentFeeModal } from "./_components/ReturnedPaymentFeeModal";
import { CancelOrderDialog } from "./_components/CancelOrderDialog";
import { RecordBankReturnDialog } from "./_components/RecordBankReturnDialog";
import { returnedPaymentFeeError } from "./_helpers/returnedPaymentFeeError";
import { paymentReturnRefusal } from "./_helpers/paymentReturnRefusal";
import { transitionBlockedReason } from "./_helpers/transitionBlockedReason";
import { recordedPiecesPerUnit } from "@utils/recordedPiecesPerUnit";
import { InvoiceDownload } from "@components/Invoice/InvoiceDownload";
import { InvoiceVoidedBadge } from "@components/Invoice/InvoiceVoidedBadge";
import { AdminDrawer } from "@admin/_components/AdminDrawer";
import { OrderHistory } from "./_components/OrderHistory";

// Inline copy for an uncontracted fee-charge failure (network error, 500, …).
// Cautious by design: the outcome of the attempt is unknown, so it must not
// invite a retry.
const GENERIC_FEE_FAILURE =
    "The charge didn't complete. Verify with the payment provider whether the fee was charged before trying again.";

const CANCEL_FAILURE = failedChangeMessage("The order was not cancelled.");

// Inline copy for a record-return failure that isn't terminal (500, network;
// a 404 or 409 is, see paymentReturnRefusal). The outcome may be unknown, so it
// points at the reloaded page. A retry is safe: if the first attempt did
// record the return, the API refuses the second with a 409.
const RECORD_RETURN_FAILURE = failedChangeMessage(
    "The return may not have been recorded.",
);

export const OrderDetail = () => {
    const { id = "" } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const {
        getAdminOrder,
        transitionOrder,
        chargeReturnedPaymentFee,
        recordPaymentReturn,
    } = useOrderApi();
    const { showSuccess } = useNotification();

    // The detail atom is app-scoped and outlives a move to another order
    // until that order's read lands: only THIS order's detail is shown, so
    // order B's page never paints order A's (#407). Compared as GUIDs: an id
    // pasted in upper case is the same order.
    const detail = useAtomValue(adminOrderDetailAtom);
    const order = detail && isSameGuid(detail.id, id) ? detail : null;
    const isFetching = useAtomValue(isFetchingAdminOrderAtom);
    const isProcessing = useAtomValue(isProcessingOrderAtom);

    // The cancel or shipped dialog that is open, with the order it was opened
    // for (null = closed).
    const [pendingDialog, setPendingDialog] = useState<{
        action: OrderTransitionAction;
        orderId: string;
    } | null>(null);
    const pendingAction = pendingDialog?.action ?? null;

    const [cancelError, setCancelError] = useState<string | null>(null);
    const reasonIdPrefix = useId();
    const feeUnconfirmedNoticeId = `${reasonIdPrefix}-fee-unconfirmed`;

    // The order the fee dialog was opened for (null = closed).
    const [feeDialogOrderId, setFeeDialogOrderId] = useState<string | null>(
        null,
    );
    const isFeeModalOpen = feeDialogOrderId !== null;
    // The last charge failure's message; see returnedPaymentFeeError for
    // showsWithUnconfirmedNotice.
    const [feeError, setFeeError] = useState<{
        message: string;
        showsWithUnconfirmedNotice: boolean;
    } | null>(null);
    // A failure whose copy says not to try again (the fee may have been, or
    // was, collected — see returnedPaymentFeeError) blocks Charge Fee for the
    // order at once. #469: unconfirmed, held and charged-but-not-saved also
    // leave the server's marker on the order (shown below; it disables Charge
    // Fee too), but the page sees it only once its reload lands, and
    // charge_not_recorded may leave nothing on the order at all — so the
    // block stands beside the marker (round 1, findings 1+2). Held per order
    // in feeChargeBlocksAtom for the SESSION: closing the dialog, leaving the
    // order or blocking another one does not lift it; sign-out and a full page
    // reload do, and so does a fee clear on Unconfirmed Charges unless the
    // block outlives a clear (charge_not_recorded, round 1b). Shown in the dialog
    // whenever it opens, so its disabled Charge Fee is never unexplained.
    // Keyed by the normalized GUID, on write and read: an upper-case URL is
    // the same order (PR 369).
    const feeChargeBlocks = useAtomValue(feeChargeBlocksAtom);
    const setFeeChargeBlocks = useSessionSetAtom(feeChargeBlocksAtom);
    const feeChargeBlockMessage =
        feeChargeBlocks[normalizeGuid(id)]?.message ?? null;
    // #469: a fee charge on this order is unconfirmed on the server. It is
    // resolved on Billing › Unconfirmed Charges, never by charging again here.
    // A lost race leaves it beside a recorded fee, and the admin must still
    // resolve it. `undefined` = an API from before the marker.
    const isFeeUnconfirmed =
        (order?.returnedPaymentFeeUnconfirmedAt ?? null) !== null;

    // The order the record-return dialog was opened for (null = closed).
    const [returnDialogOrderId, setReturnDialogOrderId] = useState<
        string | null
    >(null);
    const isReturnDialogOpen = returnDialogOrderId !== null;
    const [returnError, setReturnError] = useState<string | null>(null);
    const [isReturnRefused, setIsReturnRefused] = useState(false);
    // The "Order History" drawer (O4).
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    // A dialog's failure reason, carried onto the error page when the reload
    // behind the dialog fails too (the error page replaces the dialog). The
    // cancel and record-return dialogs reload behind a failure; the fee
    // charge's reload never brings the error page (see handleFeeConfirm), and
    // the shipped dialog does not reload. Kept with the order it is about: a
    // dialog's request on order A can fail after the page has moved to order
    // B, and that is no notice for B's error page.
    const [dialogFailureNotice, setDialogFailureNotice] = useState<{
        orderId: string;
        title: string;
        message: string;
    } | null>(null);
    // Where the record dialog returns focus once its trigger is disabled or
    // gone, instead of <body> (PR 361 review F2).
    const paymentHeadingRef = useRef<HTMLHeadingElement>(null);
    // One mutating request at a time: two clicks in one tick would otherwise
    // fire two POSTs (two fee charges, or a cancel and a refused re-cancel).
    const runGuarded = useSubmitGuard();

    // Closes every dialog, clearing its error and refusal state.
    const closeDialogs = useCallback(() => {
        // pendingDialog holds both the cancel and the shipped dialog.
        setPendingDialog(null);
        setCancelError(null);
        setFeeDialogOrderId(null);
        setFeeError(null);
        setReturnDialogOrderId(null);
        setReturnError(null);
        setIsReturnRefused(false);
        setIsHistoryOpen(false);
    }, []);

    // A dialog belongs to the order it was opened on (PR 369): the route
    // reuses this page for another order, and a cancel, shipment, fee charge
    // or return confirmed there would act on the other order. So every dialog
    // closes in the render that sees the new id.
    const [dialogsOrderId, setDialogsOrderId] = useState(id);
    if (!isSameGuid(dialogsOrderId, id)) {
        setDialogsOrderId(id);
        closeDialogs();
    }
    // The page's order right now, for a handler that resumes after an await:
    // a request for an order the page has left changes nothing on it.
    const routeOrderId = useRef(id);
    useLayoutEffect(() => {
        routeOrderId.current = id;
    }, [id]);
    const isStillOn = (orderId: string) =>
        isSameGuid(orderId, routeOrderId.current);

    // Every error-page render comes from a failure here, so this is where
    // EVERY dialog is reset (closed, no error): the error page replaces them,
    // and none may come back stale after Retry or a move to another order.
    // Only the LATEST read's failure resets them (PR 369 review 5): an older
    // read (another order's, or one a Retry superseded) failing late must not
    // close a dialog the admin has since opened. useMountRead hands the read
    // `isCurrent`, which turns false once the hook issues a newer read.
    const readDetail = useCallback(async (
        isCurrent: () => boolean,
    ): Promise<void> => {
        if (!id) {
            return;
        }
        setDialogFailureNotice(null);
        try {
            await getAdminOrder(id);
        } catch (error) {
            if (!isCurrent()) {
                throw error;
            }
            closeDialogs();
            throw error;
        }
    }, [id, getAdminOrder, closeDialogs]);

    // Issued on mount and for every order the route moves to. The page shows
    // its error page only for its own read of THIS order: a failure loading
    // order A must not paint on order B (#407). `fetchDetail` resolves to
    // whether the order loaded.
    const { readStatus, reload: fetchDetail } = useMountRead(readDetail);

    // Each confirm acts on the order its dialog was opened for, and only
    // while the page still shows it: a confirm for an order the page has left
    // posts nothing, and a request that answers after the page has moved on
    // changes nothing on it.
    const handleTransitionConfirm = async (
        trackingNumber?: string,
        carrier?: string,
    ) => {
        const opened = pendingDialog;
        if (!opened || !isStillOn(opened.orderId)) {
            return;
        }

        const request: TransitionOrderRequest = {
            toStatus: opened.action.targetStatus,
            trackingNumber,
            carrier,
        };

        await transitionOrder(opened.orderId, request);
        if (!isStillOn(opened.orderId)) {
            return;
        }
        setPendingDialog(null);
        fetchDetail();
    };

    const handleCancelConfirm = (reason: string) =>
        runGuarded(async () => {
            const opened = pendingDialog;
            if (!opened || !isStillOn(opened.orderId)) {
                return;
            }
            try {
                await transitionOrder(opened.orderId, {
                    toStatus: OrderStatusType.Cancelled,
                    notes: reason,
                });
            } catch {
                if (!isStillOn(opened.orderId)) {
                    return;
                }
                // The dialog stays open with the failure; the page reloads
                // behind it, so the current status and reasons show. If that
                // reload fails, the failure goes onto the error page.
                setCancelError(CANCEL_FAILURE);
                void fetchDetail().then((loaded) => {
                    if (!loaded) {
                        setDialogFailureNotice({
                            orderId: opened.orderId,
                            title: "Cancel order",
                            message: CANCEL_FAILURE,
                        });
                    }
                });
                return;
            }
            if (!isStillOn(opened.orderId)) {
                return;
            }
            setPendingDialog(null);
            fetchDetail();
        });

    const handleFeeConfirm = (amount: number) =>
        runGuarded(async () => {
            const openedFor = feeDialogOrderId;
            if (openedFor === null || !isStillOn(openedFor)) {
                return;
            }
            // Defense in depth beyond the disabled Charge Fee, as checkout's
            // handlePlaceOrder re-checks: a blocked order is never charged, however
            // the submit got here.
            if (feeChargeBlockMessage !== null || isFeeUnconfirmed) return;
            try {
                await chargeReturnedPaymentFee(openedFor, { amount });
                if (isStillOn(openedFor)) {
                    setFeeDialogOrderId(null);
                    setFeeError(null);
                }
                // Said even after the page has moved on: the fee was charged.
                showSuccess({ message: "Returned-payment fee charged." });
                if (isStillOn(openedFor)) {
                    fetchDetail();
                }
            } catch (error) {
                // Contracted 402/409/500 outcomes render inline in the
                // still-open dialog. An uncontracted failure (already toasted
                // by useApi) maps to undefined — fall back to GENERIC copy,
                // never null: the dialog must never be left open with a
                // failure and no copy (2026-08-28 review F1). A do-not-retry
                // outcome also locks Charge Fee for this order.
                const failure = returnedPaymentFeeError(error);
                const message = failure?.message ?? GENERIC_FEE_FAILURE;
                // The order may now carry its fee or the claim's marker. The
                // helper says so for a contracted outcome; after any other
                // 402, a server failure or a dropped connection (no status:
                // the request may have claimed the order before it died) the
                // claim may stand too (#469).
                const status = asApiFailure(error)?.status;
                const reloadsOrder = failure
                    ? failure.reloadsOrder
                    : status === undefined || status === 402 || status >= 500;
                // Recorded for the order the charge was for, even after the
                // page has moved on: that order may have been charged, and
                // must stay blocked when the admin returns to it.
                if (failure?.blocksRecharge) {
                    setFeeChargeBlocks((blocks) => ({
                        ...blocks,
                        [normalizeGuid(openedFor)]: {
                            message: failure.message,
                            ...(failure.transactionId
                                ? { transactionId: failure.transactionId }
                                : {}),
                            outlivesClear: failure.blockOutlivesClear,
                        },
                    }));
                }
                if (!isStillOn(openedFor)) {
                    return;
                }
                setFeeError({
                    message,
                    showsWithUnconfirmedNotice:
                        failure?.showsWithUnconfirmedNotice ?? false,
                });
                // Reload, so the server's state hides Charge and shows the fee
                // row, or disables Charge beside the unconfirmed notice. In
                // the background, not through the page's own read: a failed
                // reload leaves the dialog, its message and the order already
                // loaded as they were, never the error page in their place
                // (round 1, finding 6).
                if (reloadsOrder) {
                    getAdminOrder(openedFor).catch(() => {
                        /* the loaded order stays */
                    });
                }
            }
        });

    const handleRecordReturnConfirm = () =>
        runGuarded(async () => {
            const openedFor = returnDialogOrderId;
            if (openedFor === null || !isStillOn(openedFor)) {
                return;
            }
            try {
                await recordPaymentReturn(openedFor);
            } catch (error) {
                if (!isStillOn(openedFor)) {
                    return;
                }
                // The dialog stays open with the failure while the page
                // reloads behind it. A terminal failure (409: the order
                // changed since it loaded; 404: it is gone) would only repeat,
                // so Record return stays disabled; any other may be retried.
                const refusal = paymentReturnRefusal(error);
                const reason = refusal ?? RECORD_RETURN_FAILURE;
                setReturnError(reason);
                setIsReturnRefused(refusal !== undefined);
                // Not awaited, so the submit guard is released now.
                void fetchDetail().then((loaded) => {
                    if (!loaded) {
                        setDialogFailureNotice({
                            orderId: openedFor,
                            title: "Record bank return",
                            message: reason,
                        });
                    }
                });
                return;
            }
            if (isStillOn(openedFor)) {
                setReturnDialogOrderId(null);
            }
            // Only what is known: the reloaded page shows the payment's state.
            // Said even after the page has moved on: the return was recorded.
            showSuccess({ message: "Bank return recorded." });
            void fetchDetail();
        });

    const handleSimpleTransition = async (action: OrderTransitionAction) => {
        const request: TransitionOrderRequest = {
            toStatus: action.targetStatus,
        };

        await transitionOrder(id, request);
        fetchDetail();
    };

    if (readStatus === "failed") {
        return (
            <div className="px-10">
                {dialogFailureNotice?.orderId === id && (
                    <Alert
                        color={ComponentColor.Error}
                        title={dialogFailureNotice.title}
                        className="mt-6"
                    >
                        {dialogFailureNotice.message}
                    </Alert>
                )}
                <ErrorState onRetry={fetchDetail} />
            </div>
        );
    }

    if (!order) {
        return (
            <div className="flex justify-center px-10 py-20">
                <Spinner />
                <span className="sr-only">
                    {isFetching ? "Loading order" : "No order"}
                </span>
            </div>
        );
    }

    // A transition the API would refuse for this payment status renders
    // disabled, with its reason listed under the actions and linked to the
    // button through aria-describedby (#442, #452).
    const transitions = (ALLOWED_TRANSITIONS[order.status] ?? []).map(
        (action) => {
            const blockedReason = transitionBlockedReason(
                action,
                order.paymentStatus,
            );
            return {
                action,
                blockedReason,
                reasonId: blockedReason
                    ? `${reasonIdPrefix}-${action.key}`
                    : undefined,
            };
        },
    );
    const blockedReasons = transitions.flatMap(({ blockedReason, reasonId }) =>
        blockedReason && reasonId ? [{ id: reasonId, text: blockedReason }] : [],
    );

    // The fee is chargeable once per order, only after a failed payment. A
    // strict null check is deliberate: `undefined` means a pre-#298 API payload,
    // where the fee endpoint doesn't exist either — hide the action.
    const canChargeFee =
        order.paymentStatus === PaymentStatusType.Failed &&
        order.returnedPaymentFeeTransactionId === null;

    // A bank return the ACH poller missed is recorded by hand (C-10), which the
    // API allows only on a captured eCheck payment of an order that isn't
    // cancelled. Strict equality: an absent paymentMethod (pre-#298 payload)
    // hides the action.
    const canRecordReturn =
        order.paymentMethod === PaymentMethodType.BankAccount &&
        order.paymentStatus === PaymentStatusType.Captured &&
        order.status !== OrderStatusType.Cancelled;

    // Normalized once so the JSX below keeps single-line conditions
    // (`undefined` = pre-#298 API payload).
    const surchargeAmount = order.surchargeAmount ?? 0;
    const returnedFeeAmount = order.returnedPaymentFeeAmount ?? null;
    // The unconfirmed-fee notice's opening, on the page and in the fee dialog.
    const feeUnconfirmedLead =
        (order.returnedPaymentFeeTransactionId ?? null) === null
            ? "The fee charge is unconfirmed."
            : "A fee is recorded, but another fee charge on this order is still unconfirmed.";
    // What the fee dialog says. A disabled Charge Fee always says why (round
    // 1, finding 3): the block's message, or the marker's notice in place of
    // whatever the dialog last said (a decline's "try again" included) —
    // followed by that message when it says nothing was charged
    // (fee_unconfirmed, round 1b).
    const feeUnconfirmedNotice = `${feeUnconfirmedLead} Resolve it in Billing › Unconfirmed Charges.`;
    const feeDialogMessage =
        feeChargeBlockMessage ??
        (!isFeeUnconfirmed
            ? (feeError?.message ?? null)
            : feeError?.showsWithUnconfirmedNotice
                ? `${feeUnconfirmedNotice} ${feeError.message}`
                : feeUnconfirmedNotice);

    return (
        <div className="px-10 pb-12 text-neutral">
            {/* Header */}
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Title>Order {order.orderNumberFormatted}</Title>
                    <StatusBadge status={order.status} />
                    {/* #216: Download Invoice beside the header. A null
                        (none issued) reads as a sentence here — the admin can
                        act on it. */}
                    <InvoiceDownload invoice={order.invoice} variant="admin" />
                    <InvoiceVoidedBadge invoice={order.invoice} />
                </div>
                <div className="flex flex-wrap gap-2">
                    {transitions.map(({ action, blockedReason, reasonId }) => (
                        <Button
                            key={action.key}
                            color={
                                action.targetStatus === OrderStatusType.Cancelled
                                    ? ComponentColor.Error
                                    : action.targetStatus === OrderStatusType.Delivered
                                        ? ComponentColor.Success
                                        : ComponentColor.Primary
                            }
                            size={ComponentSize.SM}
                            disabled={isProcessing || blockedReason !== null}
                            aria-describedby={reasonId}
                            onClick={() => {
                                if (action.targetStatus === OrderStatusType.Cancelled) {
                                    // Irreversible: confirmed in a dialog (#452).
                                    setCancelError(null);
                                    setPendingDialog({ action, orderId: id });
                                } else if (action.requiresShipment) {
                                    setPendingDialog({ action, orderId: id });
                                } else {
                                    handleSimpleTransition(action).catch(
                                        () => { /* isProcessing reset drives UI */ },
                                    );
                                }
                            }}
                        >
                            {action.label}
                        </Button>
                    ))}
                    <Button
                        color={ComponentColor.Ghost}
                        size={ComponentSize.SM}
                        onClick={() => navigate(ROUTES.ADMIN_ORDERS)}
                    >
                        Back to Orders
                    </Button>
                </div>
            </div>
            {blockedReasons.length > 0 && (
                <ul className="-mt-3 mb-6 space-y-1 text-sm text-neutral/70">
                    {blockedReasons.map((reason) => (
                        <li key={reason.id} id={reason.id}>
                            {reason.text}
                        </li>
                    ))}
                </ul>
            )}

            <div className="grid gap-6 lg:grid-cols-2">
                {/* Order Info */}
                <DetailSection title="Order Info">
                    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <DetailField label="Order Number">
                            {order.orderNumberFormatted}
                        </DetailField>
                        <DetailField label="Status">
                            <StatusBadge status={order.status} />
                        </DetailField>
                        <DetailField label="Created">
                            {formatDateTime(order.createdAt)}
                        </DetailField>
                        <DetailField label="Last Updated">
                            {formatDateTime(order.lastUpdatedAt)}
                        </DetailField>
                        {order.trackingNumber && (
                            <DetailField label="Tracking Number">
                                {order.trackingNumber}
                            </DetailField>
                        )}
                        {order.carrier && (
                            <DetailField label="Carrier">
                                {order.carrier}
                            </DetailField>
                        )}
                    </dl>
                    <Button
                        color={ComponentColor.Primary}
                        outline
                        size={ComponentSize.SM}
                        className="mt-4"
                        onClick={() => setIsHistoryOpen(true)}
                    >
                        <Icon name="history" />
                        View History
                    </Button>
                </DetailSection>

                {/* Customer */}
                <DetailSection title="Customer">
                    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <DetailField label="Organization">
                            <Link
                                to={ROUTES.ADMIN_CLIENTS_DETAIL(
                                    order.organizationId,
                                )}
                                className="link link-primary"
                            >
                                {order.organizationName}
                            </Link>
                        </DetailField>
                        <DetailField label="Contact">{order.userName}</DetailField>
                        <DetailField label="Email">{order.userEmail}</DetailField>
                        <DetailField label="Location">{order.locationName}</DetailField>
                    </dl>
                </DetailSection>

                {/* Shipping Address */}
                <DetailSection title="Shipping Address">
                    <address className="not-italic text-sm">
                        {order.shippingStreetAddress}
                        <br />
                        {order.shippingCity}, {order.shippingState}{" "}
                        {order.shippingZipCode}
                        <br />
                        {order.shippingCountry}
                    </address>
                </DetailSection>

                {/* Pricing */}
                <DetailSection title="Pricing">
                    <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <DetailField label="Subtotal">
                            {currencyFormatter.format(order.subtotal)}
                        </DetailField>
                        <DetailField label="Shipping">
                            {currencyFormatter.format(order.shippingCost)}
                        </DetailField>
                        <DetailField label="Tax">
                            {currencyFormatter.format(order.tax)}
                        </DetailField>
                        {order.discountTotal > 0 && (
                            <DetailField label="Discount">
                                -{currencyFormatter.format(order.discountTotal)}
                            </DetailField>
                        )}
                        <DetailField label="Total">
                            <span className="text-base font-bold">
                                {currencyFormatter.format(order.total)}
                            </span>
                        </DetailField>
                    </dl>
                </DetailSection>

                {/* Payment */}
                <DetailSection title="Payment" headingRef={paymentHeadingRef}>
                    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <DetailField label="Status">
                            {paymentStatusLabel(order.paymentStatus)}
                        </DetailField>
                        <DetailField label="Method">
                            {paymentMethodLabel(order.paymentMethod)}
                        </DetailField>
                        <DetailField label="Amount Paid">
                            {order.amountPaid !== null &&
                            order.amountPaid !== undefined
                                ? currencyFormatter.format(order.amountPaid)
                                : "—"}
                        </DetailField>
                        {surchargeAmount > 0 && (
                            <DetailField label="Card Surcharge">
                                {currencyFormatter.format(surchargeAmount)}
                            </DetailField>
                        )}
                        <DetailField label="Card">
                            {order.paymentMethodBrand && order.paymentMethodLast4
                                ? `${order.paymentMethodBrand} ···· ${order.paymentMethodLast4}`
                                : "—"}
                        </DetailField>
                        <DetailField label="Transaction ID">
                            {order.paymentTransactionId ?? "—"}
                        </DetailField>
                        {returnedFeeAmount !== null && (
                            <DetailField label="Returned-Payment Fee">
                                <span className="text-sm">
                                    Charged{" "}
                                    {currencyFormatter.format(returnedFeeAmount)}{" "}
                                    on{" "}
                                    {formatDateTime(
                                        order.returnedPaymentFeeChargedAt ??
                                            null,
                                    )}
                                    {order.returnedPaymentFeeTransactionId
                                        ? ` — Txn ${order.returnedPaymentFeeTransactionId}`
                                        : ""}
                                </span>
                            </DetailField>
                        )}
                    </dl>
                    {isFeeUnconfirmed && (
                        <p id={feeUnconfirmedNoticeId} className="mt-4 text-sm">
                            {feeUnconfirmedLead} Resolve it in{" "}
                            <Link
                                to={ROUTES.ADMIN_UNCONFIRMED_CHARGES}
                                className="link link-primary"
                            >
                                Billing › Unconfirmed Charges
                            </Link>
                            .
                        </p>
                    )}
                    {canChargeFee && (
                        <div className="mt-4">
                            <Button
                                color={ComponentColor.Error}
                                size={ComponentSize.SM}
                                // Also disabled while the post-success refetch
                                // is in flight: until it settles the stale
                                // order still says canChargeFee, and an
                                // enabled button would invite a second POST
                                // (2026-08-28 review F4).
                                disabled={
                                    isProcessing || isFetching || isFeeUnconfirmed
                                }
                                aria-describedby={
                                    isFeeUnconfirmed
                                        ? feeUnconfirmedNoticeId
                                        : undefined
                                }
                                onClick={() => {
                                    setFeeError(null);
                                    setFeeDialogOrderId(id);
                                }}
                            >
                                Charge Returned-Payment Fee
                            </Button>
                        </div>
                    )}
                    {canRecordReturn && (
                        <div className="mt-4">
                            <Button
                                // Outline, not solid: it shows on every
                                // captured eCheck order and is rarely needed,
                                // so it must not read as an alarm.
                                color={ComponentColor.Error}
                                outline
                                size={ComponentSize.SM}
                                // Disabled through the post-success refetch
                                // too: the stale order still qualifies.
                                disabled={isProcessing || isFetching}
                                onClick={() => {
                                    setReturnError(null);
                                    setIsReturnRefused(false);
                                    setReturnDialogOrderId(id);
                                }}
                            >
                                Record Bank Return
                            </Button>
                        </div>
                    )}
                </DetailSection>
            </div>

            {/* Line Items */}
            <div className="mt-6">
                <DetailSection title="Items">
                    <div className="overflow-x-auto">
                        <table className="table table-sm w-full">
                            <thead>
                                <tr>
                                    <th>Product</th>
                                    <th>Variant</th>
                                    <th>UPC</th>
                                    <th className="text-right">Qty</th>
                                    <th className="text-right">Pieces per Unit</th>
                                    <th className="text-right">Unit Price</th>
                                    <th className="text-right">Line Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {order.products.map((p) => {
                                    // Orders 6: the count the order recorded,
                                    // not the catalogue's today. A piece price
                                    // shows only for more than one piece.
                                    const pieces = recordedPiecesPerUnit(p.piecesPerUnit);
                                    const pricing = formatPiecePricingFromCount(p.price, pieces);
                                    return (
                                        <tr key={`${p.orderId}-${p.productId}-${p.skuId}`}>
                                            <td>
                                                <div className="flex items-center gap-3">
                                                    {p.imageUrl && (
                                                        <img
                                                            src={p.imageUrl}
                                                            alt={p.name}
                                                            className="h-10 w-10 rounded object-cover"
                                                        />
                                                    )}
                                                    <span>{p.name}</span>
                                                </div>
                                            </td>
                                            <td>
                                                {p.variant ? (
                                                    <span>
                                                        {p.variant.optionValues
                                                            .map(
                                                                (ov) =>
                                                                    `${ov.optionName}: ${ov.value}`,
                                                            )
                                                            .join(", ")}
                                                    </span>
                                                ) : (
                                                    "—"
                                                )}
                                            </td>
                                            <td>{p.upc}</td>
                                            <td className="text-right">{p.quantity}</td>
                                            <td className="text-right">
                                                {pieces ?? "—"}
                                            </td>
                                            <td className="text-right">
                                                {pricing.unitPrice}
                                                {pricing.showPiece && (
                                                    <div className="text-xs text-neutral/70">
                                                        {pricing.piecePrice} / piece
                                                    </div>
                                                )}
                                            </td>
                                            <td className="text-right">
                                                {currencyFormatter.format(
                                                    p.price * p.quantity,
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </DetailSection>
            </div>

            {/* Shipped modal */}
            {pendingAction?.requiresShipment && (
                <ShippedModal
                    isProcessing={isProcessing}
                    onConfirm={(trackingNumber, carrier) => {
                        handleTransitionConfirm(
                            trackingNumber,
                            carrier,
                        ).catch(() => { /* error atom drives UI */ });
                    }}
                    onClose={() => setPendingDialog(null)}
                />
            )}

            {/* Cancel confirmation (#452) */}
            {pendingAction?.targetStatus === OrderStatusType.Cancelled && (
                <CancelOrderDialog
                    orderNumber={order.orderNumberFormatted}
                    isProcessing={isProcessing}
                    errorMessage={cancelError}
                    onConfirm={(reason) => void handleCancelConfirm(reason)}
                    onClose={() => setPendingDialog(null)}
                />
            )}

            {/* Returned-payment fee modal (#298) */}
            {isFeeModalOpen && (
                <ReturnedPaymentFeeModal
                    isProcessing={isProcessing}
                    errorMessage={feeDialogMessage}
                    isChargeBlocked={
                        feeChargeBlockMessage !== null || isFeeUnconfirmed
                    }
                    onConfirm={(amount) => void handleFeeConfirm(amount)}
                    onClose={() => {
                        setFeeDialogOrderId(null);
                        setFeeError(null);
                    }}
                />
            )}

            {/* Record bank return confirmation (C-10) */}
            {isReturnDialogOpen && (
                <RecordBankReturnDialog
                    customerName={order.organizationName}
                    isProcessing={isProcessing}
                    errorMessage={returnError}
                    isRefused={isReturnRefused}
                    returnFocusRef={paymentHeadingRef}
                    onConfirm={() => void handleRecordReturnConfirm()}
                    onClose={() => setReturnDialogOrderId(null)}
                />
            )}

            {/* Order History (O4). The history comes with this page's own
                read. It says it is refreshing while a status change's POST
                is out (a one-click change re-reads only once it answers) and
                while the re-read is in flight. */}
            {isHistoryOpen && (
                <AdminDrawer
                    title="Order History"
                    onClose={() => setIsHistoryOpen(false)}
                >
                    <OrderHistory
                        history={order.statusHistory}
                        isRefreshing={
                            isProcessing || readStatus === "loading"
                        }
                    />
                </AdminDrawer>
            )}
        </div>
    );
};
